import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useSelection } from '../context/SelectionContext';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Users, ChevronRight, Check } from 'lucide-react';
import GuidedWizard from './GuidedWizard';
import ExperienceDetailPopup from './ExperienceDetailPopup';
import { SubHeader, subHeaderSpacerHeight } from './SubHeader';
import { useTabletMode } from '../context/TabletModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SLOT_LABELS = {
  manana: { es: 'Mañana', en: 'Morning', cat: 'Matí', fr: 'Matin', hours: '9–12h' },
  vermut: { es: 'Vermut', en: 'Vermouth', cat: 'Vermut', fr: 'Vermouth', hours: '12–14h' },
  comida: { es: 'Comida', en: 'Lunch', cat: 'Dinar', fr: 'Déjeuner', hours: '14–17h' },
  tarde: { es: 'Tarde', en: 'Afternoon', cat: 'Tarda', fr: 'Après-midi', hours: '17–20h' },
  noche: { es: 'Noche', en: 'Evening', cat: 'Nit', fr: 'Soirée', hours: '20–24h' },
};

const ExperiencesShowcase = ({ onBack }) => {
  const { isTablet } = useTabletMode();
  const { language, localize } = useLanguage();
  const { isDark } = useTheme();
  const { addItem } = useSelection();
  const [experiences, setExperiences] = useState([]);
  const [slots, setSlots] = useState([]);
  const [currentSlot, setCurrentSlot] = useState('');
  const [loading, setLoading] = useState(true);
  const [wizardProduct, setWizardProduct] = useState(null);
  const [detailExperience, setDetailExperience] = useState(null);
  const [now, setNow] = useState(new Date());

  // Live clock — updates every 30 seconds for countdown
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodsRes, momentoRes, configRes] = await Promise.all([
          fetch(`${API_URL}/api/products`),
          fetch(`${API_URL}/api/products/momento/actual`),
          fetch(`${API_URL}/api/admin/momento/public/config`),
        ]);

        if (prodsRes.ok) {
          const prods = await prodsRes.json();
          setExperiences(prods.filter(p => p.product_type === 'experience' && p.is_active !== false));
        }
        if (momentoRes.ok) {
          const momento = await momentoRes.json();
          setCurrentSlot(momento.slot_id);
        }
        if (configRes.ok) {
          const config = await configRes.json();
          setSlots(config.slots || []);
        }
      } catch (e) {
        console.error('Error fetching experiences:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const isActiveNow = (expId) => {
    const slot = slots.find(s => s.experience_product_id === expId);
    return slot?.slot_id === currentSlot && slot?.is_active !== false;
  };

  const getSlotForExp = (expId) => {
    return slots.find(s => s.experience_product_id === expId);
  };

  // Calculate countdown text for locked experiences
  const getCountdown = useCallback((slot) => {
    if (!slot || slot.start_hour == null) return null;
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();
    const nowMins = currentHour * 60 + currentMin;
    const targetMins = slot.start_hour * 60;

    let diffMins = targetMins - nowMins;
    if (diffMins <= 0) diffMins += 24 * 60; // tomorrow

    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;

    const isTomorrow = targetMins <= nowMins;

    if (language === 'es') {
      if (isTomorrow) return `Mañana a las ${slot.start_hour}h`;
      if (hours === 0) return `En ${mins} min`;
      if (mins === 0) return `En ${hours}h`;
      return `En ${hours}h ${mins}min`;
    } else if (language === 'en') {
      if (isTomorrow) return `Tomorrow at ${slot.start_hour}h`;
      if (hours === 0) return `In ${mins} min`;
      if (mins === 0) return `In ${hours}h`;
      return `In ${hours}h ${mins}min`;
    } else if (language === 'cat') {
      if (isTomorrow) return `Demà a les ${slot.start_hour}h`;
      if (hours === 0) return `En ${mins} min`;
      if (mins === 0) return `En ${hours}h`;
      return `En ${hours}h ${mins}min`;
    } else {
      if (isTomorrow) return `Demain à ${slot.start_hour}h`;
      if (hours === 0) return `Dans ${mins} min`;
      if (mins === 0) return `Dans ${hours}h`;
      return `Dans ${hours}h ${mins}min`;
    }
  }, [now, language]);

  const handleAdd = (exp) => {
    const hasOptions = exp.options?.length > 0;
    const needsSelection = exp.requires_selection;
    if (hasOptions || needsSelection) {
      setWizardProduct(exp);
      return;
    }
    // No options — show detail popup confirming the items breakdown
    setDetailExperience(exp);
  };

  const loc = (field) => field?.[language] || field?.es || '';

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : 'var(--sand-50)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#B78C3C' }} />
      </div>
    );
  }

  return (
    <div
      className="min-h-dvh safe-bottom transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#1a1a1a' : 'var(--sand-50)' }}
      data-testid="experiences-showcase"
    >
      {/* Fixed header */}
      <SubHeader
        testid="experiences-showcase-header"
        onBack={onBack}
        backLabel={language === 'es' ? 'Volver' : language === 'en' ? 'Back' : language === 'cat' ? 'Tornar' : 'Retour'}
        title={language === 'es' ? 'Experiencias' : language === 'en' ? 'Experiences' : language === 'cat' ? 'Experiències' : 'Expériences'}
      />

      <div style={{ height: subHeaderSpacerHeight(isTablet) }} />

      {/* Intro */}
      <div className="px-4 mb-6 max-w-5xl mx-auto text-center">
        <p className="text-sm" style={{ color: isDark ? '#aaa' : 'var(--stone-500)' }}>
          {language === 'es' ? 'Selecciones completas para vivir Pont10' :
           language === 'en' ? 'Complete selections to experience Pont10' :
           language === 'cat' ? 'Seleccions completes per viure Pont10' :
           'Sélections complètes pour vivre Pont10'}
        </p>
      </div>

      {/* Experience cards */}
      <div className="px-4 pb-8 max-w-5xl mx-auto space-y-5">
        {experiences.length === 0 && (
          <div className="text-center py-16" data-testid="no-experiences">
            <p className="font-serif text-xl mb-2" style={{ color: isDark ? '#f5f5f5' : 'var(--stone-800)' }}>
              {language === 'es' ? 'Cargando experiencias...' :
               language === 'en' ? 'Loading experiences...' :
               language === 'cat' ? 'Carregant experiències...' :
               'Chargement des expériences...'}
            </p>
            <p className="text-sm" style={{ color: isDark ? '#888' : 'var(--stone-500)' }}>
              {language === 'es' ? 'Si persiste, contacta al personal' :
               language === 'en' ? 'If it persists, contact staff' :
               language === 'cat' ? 'Si persisteix, contacta el personal' :
               'Si cela persiste, contactez le personnel'}
            </p>
          </div>
        )}
        {experiences.filter(exp => getSlotForExp(exp.id)?.is_active !== false).map((exp) => {
          const active = isActiveNow(exp.id);
          const slot = getSlotForExp(exp.id);
          const slotInfo = slot ? SLOT_LABELS[slot.slot_id] : null;

          return (
            <div
              key={exp.id}
              className="rounded-2xl overflow-hidden shadow-lg transition-all"
              style={{
                backgroundColor: isDark ? '#2a2a2a' : '#fff',
                border: active ? '2px solid rgba(183,140,60,0.4)' : '1px solid rgba(0,0,0,0.08)',
              }}
              data-testid={`experience-card-${exp.id}`}
            >
              {/* Horizontal layout for tablets, vertical for mobile */}
              <div className="md:flex">
                {/* Image — always bright and clean, no overlays */}
                <div className="relative h-52 md:h-auto md:w-[45%] md:min-h-[260px] overflow-hidden flex-shrink-0">
                  <img
                    src={exp.images?.[0]}
                    alt={localize(exp.name)}
                    className="w-full h-full object-cover"
                  />

                {/* Active badge */}
                {active && (
                  <div
                    className="absolute top-3 left-3 px-3 py-1.5 rounded-full text-sm font-semibold text-white flex items-center gap-1.5"
                    style={{ background: 'linear-gradient(135deg, #B78C3C, #D4AF37)' }}
                    data-testid="experience-active-badge"
                  >
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    {language === 'es' ? 'Disponible ahora' : language === 'en' ? 'Available now' : language === 'cat' ? 'Disponible ara' : 'Disponible maintenant'}
                  </div>
                )}

                {/* People badge */}
                {exp.experience_target_people && exp.experience_target_people !== '1' && (
                  <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-sm font-medium text-white bg-black/40 backdrop-blur-sm flex items-center gap-1.5">
                    <Users size={14} />
                    {exp.experience_target_people}
                  </div>
                )}
              </div>

              {/* Content — price, schedule, description all here */}
              <div className="p-5 md:flex-1 md:flex md:flex-col md:justify-between">
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-serif text-2xl" style={{ color: isDark ? '#f5f5f5' : '#2C2C2A' }}>
                      {localize(exp.name)}
                    </h3>
                    <span className="text-2xl font-bold flex-shrink-0" style={{ color: '#B78C3C' }}>
                      {exp.price?.toFixed(2)}€
                    </span>
                  </div>

                  {/* Time slot info */}
                  {slotInfo && (
                    <div className="flex items-center gap-1.5 mb-3 text-base" style={{ color: active ? '#B78C3C' : (isDark ? '#888' : '#999') }}>
                      <Clock size={16} />
                      <span className="font-medium">{slotInfo[language] || slotInfo.es} · {slotInfo.hours}</span>
                    </div>
                  )}

                  {exp.experience_copy && (
                    <p className="text-base mb-3 leading-relaxed" style={{ color: isDark ? '#bbb' : '#5C5C59' }}>
                      {loc(exp.experience_copy)}
                    </p>
                  )}

                  {/* Items */}
                  <div className="space-y-2 mb-4">
                    {(exp.experience_items || []).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2.5 text-base" style={{ color: isDark ? '#ddd' : '#4C4C49' }}>
                        <span style={{ color: '#B78C3C' }}>&#10003;</span>
                        {loc(item)}
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                {active ? (
                  <button
                    onClick={() => handleAdd(exp)}
                    className="w-full py-4 rounded-xl text-white font-semibold text-lg shadow-lg active:scale-[0.97] transition-transform flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #B78C3C 0%, #D4AF37 50%, #C9A84C 100%)' }}
                    data-testid={`add-experience-${exp.id}`}
                  >
                    {language === 'es' ? 'Añadir al pedido' : language === 'en' ? 'Add to order' : language === 'cat' ? 'Afegir a la comanda' : 'Ajouter à la commande'}
                    <ChevronRight size={20} />
                  </button>
                ) : (
                  <div
                    className="w-full py-4 rounded-xl text-center font-medium text-base"
                    style={{
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      color: isDark ? '#666' : '#aaa',
                    }}
                    data-testid={`disabled-experience-${exp.id}`}
                  >
                    <Clock size={16} className="inline mr-2 -mt-0.5" />
                    {slot
                      ? (() => {
                          const countdown = getCountdown(slot);
                          const unlockLabel = language === 'es' ? 'Se desbloquea' : language === 'en' ? 'Unlocks' : language === 'cat' ? 'Es desbloqueja' : 'Se débloque';
                          return countdown ? `${unlockLabel} ${countdown.toLowerCase()}` : (language === 'es' ? 'Próximamente' : 'Coming soon');
                        })()
                      : (language === 'es' ? 'Próximamente' : 'Coming soon')
                    }
                  </div>
                )}
              </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* GuidedWizard for experience customization */}
      {wizardProduct && (
        <GuidedWizard
          product={wizardProduct}
          index={0}
          onComplete={() => setWizardProduct(null)}
          onClose={() => setWizardProduct(null)}
        />
      )}

      {/* ExperienceDetailPopup for experiences without options */}
      {detailExperience && (
        <ExperienceDetailPopup
          experience={detailExperience}
          onClose={() => setDetailExperience(null)}
        />
      )}
    </div>
  );
};

export default ExperiencesShowcase;
